import { Emergency } from "@mui/icons-material";
import { Box, Button, Paper, Tooltip, Typography } from "@mui/material";
import { NavLink } from "react-router-dom";

import { Route } from "~services/router/Route";
import { EntityAvatar } from "~ui/atoms/EntityAvatar/EntityAvatar";
import { AppLogo } from "~ui/organisms/AppLogo/AppLogo";
import { makeBubbleAvatarUrl } from "~utils/url";

const tEmergency = "Emergency";
const tLogoTooltip = "Click to return to home page";
const tSchoolVmHomeTooltip = "Go to visitor management home";

export interface VisitorManagementTopBarProps {
  isKioskLayout: boolean;
  logoHeight: string;
  onEmergency: () => void;
  schoolId: string | undefined;
  schoolName: string;
}

export function VisitorManagementTopBar({
  isKioskLayout,
  logoHeight,
  onEmergency,
  schoolId,
  schoolName,
}: VisitorManagementTopBarProps) {
  return (
    <Paper
      elevation={0}
      square
      sx={{
        alignItems: "center",
        borderBottom: 1,
        borderColor: "divider",
        display: "flex",
        flexShrink: 0,
        justifyContent: "center",
        minHeight: 56,
        position: "relative",
        px: 2,
        py: 1.5,
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          justifyContent: "flex-start",
          left: 8,
          position: "absolute",
          top: "50%",
          transform: "translateY(-50%)",
        }}
      >
        <Tooltip placement="bottom" title={tLogoTooltip}>
          <Button
            aria-label={tLogoTooltip}
            color="inherit"
            component={NavLink}
            sx={{ borderRadius: 1, minWidth: "auto", px: 1, py: 0.5 }}
            to={Route.home()}
            variant="text"
          >
            <AppLogo logo="navBarLogo" style={{ height: logoHeight }} />
          </Button>
        </Tooltip>
      </Box>
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          flexDirection: "row",
          gap: 2,
          justifyContent: "center",
          maxWidth: "100%",
          minWidth: 0,
          pl: 6,
          pr: isKioskLayout ? { sm: 18, xs: 16 } : 6,
          textAlign: "center",
        }}
      >
        {schoolId ? (
          <Tooltip placement="bottom" title={tSchoolVmHomeTooltip}>
            <Button
              aria-label={tSchoolVmHomeTooltip}
              color="inherit"
              component={NavLink}
              sx={{
                "&:hover": {
                  bgcolor: "action.hover",
                },
                alignItems: "center",
                borderRadius: 1,
                color: "inherit",
                display: "flex",
                flexDirection: "row",
                gap: 2,
                justifyContent: "center",
                maxWidth: "100%",
                minWidth: 0,
                px: 1.5,
                py: 0.5,
                textAlign: "center",
                textTransform: "none",
              }}
              end
              to={Route.schoolVisitorManagement({ schoolId })}
              variant="text"
            >
              <EntityAvatar
                canEdit={false}
                entityName={schoolName}
                imageUrl={makeBubbleAvatarUrl(schoolId)}
                imgAlt=""
                size="medium"
              />
              <Typography component="span" noWrap variant="h2">
                {schoolName}
              </Typography>
            </Button>
          </Tooltip>
        ) : (
          <Typography component="h1" noWrap variant="h2">
            {schoolName}
          </Typography>
        )}
      </Box>
      {isKioskLayout ? (
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <Button
            color="error"
            size="medium"
            startIcon={<Emergency />}
            sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
            variant="contained"
            onClick={onEmergency}
          >
            {tEmergency}
          </Button>
        </Box>
      ) : null}
    </Paper>
  );
}
